<script lang="ts" setup>
import {computed, ref} from "vue";
import {useRouter} from "vue-router";
import {ArrowRight} from "@lucide/vue";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "@/core/components/ui/dialog";
import {Button} from "@/core/components/ui/button";
import {pluralize} from "@/core/filters/Pluralize";
import useCustomerLoyaltyCardMembershipTransactions
    from "@/customer/composables/loyalty-card-membership/useCustomerLoyaltyCardMembershipTransactions";
import LoyaltyCardMembershipTransactions
    from "@/loyalty-card-membership/components/loyalty-card-membership-transactions/LoyaltyCardMembershipTransactions.vue";

/**
 * What the listing row already knows about the card: shown at once, only the
 * history is fetched.
 */
export interface LoyaltyCardMembershipHistorySummary {
    customerId: number;
    membershipId: number;
    name: string;
    initials: string;
    createdAt: string;
    frequency: string;
    fillCurrent: number;
    fillThreshold: number;
    fillPercent: number;
    fillColor: string;
    /** The unit the card objective counts in. */
    fillUnit: "produit" | "€";
}

const open = defineModel<boolean>("open", {default: false});

// Read once: the listing remounts the dialog each time it opens one.
const props = defineProps<{
    summary: LoyaltyCardMembershipHistorySummary;
}>();

const router = useRouter();

const selectedType = ref<string | null>(null);

const {transactions, isLoading, isLoadingMore, error, hasMore, loadMore, refresh} = useCustomerLoyaltyCardMembershipTransactions(
    props.summary.customerId,
    props.summary.membershipId,
    selectedType
);

/**
 * How far the card is from its reward, in the objective's unit.
 */
const remainingLabel = computed((): string => {
    const remaining = props.summary.fillThreshold - props.summary.fillCurrent;

    if (remaining <= 0) {
        return "Seuil de récompense atteint";
    }

    const unit = props.summary.fillUnit === "€" ? "€" : pluralize("produit", remaining);

    return `Encore ${remaining} ${unit} avant la récompense`;
});

/**
 * Leave the listing for the patient record, opened on this card.
 */
const openPatientRecord = () => {
    open.value = false;

    router.push({
        name: 'customer.loyalty.membership',
        params: {
            customerId: props.summary.customerId,
            membershipId: props.summary.membershipId
        }
    });
};
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent class="max-w-3xl max-h-[90vh] grid-rows-[auto_auto_minmax(0,1fr)_auto]">
      <DialogHeader class="text-left">
        <DialogTitle class="text-primary">
          Historique de la carte
        </DialogTitle>
        <DialogDescription class="sr-only">
          Achats, récompenses et crédits de la carte de fidélité de {{ summary.name }}.
        </DialogDescription>
      </DialogHeader>

      <div class="flex items-center gap-3 px-4 py-3 bg-primary-lightest rounded-xl">
        <span
          class="size-11 shrink-0 rounded-full bg-white text-primary flex items-center justify-center text-sm font-semibold"
        >
          {{ summary.initials }}
        </span>
        <div class="flex-1 min-w-0 flex flex-col gap-0.5">
          <span class="text-base font-semibold text-gray-dark truncate">{{ summary.name }}</span>
          <span class="text-sm text-slate-500">
            Carte créée le {{ summary.createdAt }} · Fréquence d'achat {{ summary.frequency }}
          </span>
        </div>
        <div v-if="summary.fillThreshold > 0" class="min-w-48 shrink-0 flex flex-col gap-1.5">
          <div class="flex justify-between text-sm">
            <span class="text-slate-500">Carte active</span>
            <span class="font-semibold text-gray-dark tabular-nums">
              {{ summary.fillCurrent }}/{{ summary.fillThreshold }}
            </span>
          </div>
          <div class="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div
              class="h-full rounded-full"
              :style="{ width: `${summary.fillPercent}%`, background: summary.fillColor }"
            />
          </div>
          <span class="text-sm text-slate-500 whitespace-nowrap">{{ remainingLabel }}</span>
        </div>
      </div>

      <div class="min-h-0 overflow-y-auto -mx-6 px-6">
        <LoyaltyCardMembershipTransactions
          embedded
          :transactions="transactions"
          :selected-type="selectedType"
          :loading="isLoading"
          :loading-more="isLoadingMore"
          :failed="!!error"
          :has-more="hasMore"
          @type-selected="type => selectedType = type"
          @load-more="loadMore"
          @retry="refresh"
        />
      </div>

      <DialogFooter class="-mx-6 px-6 pt-4 border-t border-slate-200">
        <DialogClose as-child>
          <Button variant="outline" class="border-solid">
            Fermer
          </Button>
        </DialogClose>
        <Button class="bg-primary text-white hover:bg-primary/90" @click="openPatientRecord">
          Voir la fiche patient
          <ArrowRight />
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
